import React, { FormEvent, ReactNode } from 'react';

import { background, padding, useClassnames, visuallyHidden } from '@lumapps/classnames';
import { LUMX_CHAT } from '@lumapps/lumx-chat/keys';
import {
    Alignment,
    Button,
    ButtonProps,
    ColorPalette,
    ColorVariant,
    FlexBox,
    InputLabel,
    Orientation,
    Size,
    TextField,
    TextFieldProps,
} from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { ASK_AI_FOLLOW_UP_INPUT_NAME } from '../../constants';

import './index.scss';

export interface AskAiFollowUpInputProps {
    /** Element displayed bellow the submit section (text field and button) */
    afterElement?: ReactNode;
    /**  Props to forward to the `Button` */
    buttonProps: ButtonProps;
    /** className to forward */
    className?: string;
    /** Callback executed on input change */
    onChange?: (value: string) => void;
    /** Callback executed on form submit */
    onSubmit?: (value?: string) => void;
    /**
     * Text field props
     * `onChange` and `value` are omitted for clarity.
     */
    textFieldProps?: Omit<TextFieldProps, 'onChange' | 'value'>;
    /** Whether the textfield label should not be displayed but still linked to the field */
    hideTextFieldLabel?: boolean;
}

const CLASSNAME = 'ask-ai-follow-up-input';

/**
 * Input used in the ask ai feature to ask a follow up question.
 * @family Ask Ai Conversation
 * @param AskAiFollowUpInputProps
 * @returns AskAiFollowUpInput
 */
export const AskAiFollowUpInput: React.FC<AskAiFollowUpInputProps> = ({
    afterElement,
    buttonProps,
    className,
    onChange,
    onSubmit,
    textFieldProps = {},
    hideTextFieldLabel = true,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();

    const { label, placeholder, ...forwardedTextFieldProps } = textFieldProps;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;

        /** Get userMessage value from formData  */
        const inputElement = form.elements.namedItem(ASK_AI_FOLLOW_UP_INPUT_NAME) as HTMLInputElement | null;
        const userMessage = inputElement?.value;

        if (!userMessage?.trim()) {
            return;
        }

        if (onSubmit) {
            onSubmit(userMessage);
        }

        form.reset();
    };

    return (
        <FlexBox
            as="form"
            onSubmit={handleSubmit}
            className={block({ 'with-after-element': !!afterElement }, [
                background(ColorPalette.light, ColorVariant.N),
                className,
            ])}
            orientation={Orientation.vertical}
            gap={Size.tiny}
        >
            <FlexBox
                className={element('submit-inputs')}
                orientation={Orientation.horizontal}
                vAlign={Alignment.center}
                hAlign={Alignment.center}
                gap={undefined}
            >
                <InputLabel
                    className={hideTextFieldLabel ? visuallyHidden() : undefined}
                    htmlFor="ask-ai-follow-up-input"
                >
                    {label || placeholder || translateKey(LUMX_CHAT.SEND_A_MESSAGE)}
                </InputLabel>
                <TextField
                    className={element('text-field', [padding('all', null)])}
                    id="ask-ai-follow-up-input"
                    name="ask-ai-follow-up-input"
                    enterKeyHint="send"
                    placeholder={placeholder}
                    {...forwardedTextFieldProps}
                    onChange={(textInput) => {
                        if (onChange) {
                            onChange(textInput);
                        }
                    }}
                />
                <Button type="submit" {...buttonProps} className={element('submit-button', [buttonProps?.className])} />
            </FlexBox>
            {afterElement}
        </FlexBox>
    );
};
